import { useMemo } from "react"

import { comparePosition } from "shared/lib/sorting/comparePosition"
import { findParent } from "shared/lib/treeUtils"
import { ProjectChild } from "shared/types/Project"

import { filterSiblingsByType } from "entities/project-children/lib/filterSiblingsByType"
import { useProjectData } from "entities/projects/api/useProjectData"

interface UseProjectChildPositionArgs {
  projectID: string
  child: ProjectChild | null
}

export function useProjectChildPosition({
  projectID,
  child
}: UseProjectChildPositionArgs) {
  const { project, isLoading } = useProjectData(projectID, true)

  const parent = useMemo(() => {
    if (!child) {
      return project
    }
    return isLoading
      ? undefined
      : findParent(child.id, project?.children) || project
  }, [project?.children, child])

  const displayPosition = useMemo(() => {
    if (!child || !parent || !child.__typename) {
      return 0
    }

    // Type guard: Check if parent has children property
    if (!("children" in parent) || !parent.children) {
      return 0
    }

    const siblings = filterSiblingsByType({
      siblings: parent.children,
      childType: child.__typename
    })

    const sortedSiblings = [...siblings].sort(comparePosition)
    const index = sortedSiblings.findIndex(sibling => sibling.id === child.id)

    return index !== -1 ? index : 0
  }, [child, parent])

  return {
    parent,
    position: child?.position || 0,
    displayPosition,
    isLoading
  }
}
